import React, { useEffect, useRef, useState, useCallback } from "react";
import { Power, Volume2, VolumeX, Loader2 } from "lucide-react";
import { useNowPlaying } from "../hooks/useNowPlaying";
import { claimPlayback, onPlaybackClaim } from "../lib/playbackBus";
import { playStartupSequence, playStopClick } from "../lib/vintageSfx";

/**
 * VintageRadio — a 1950s-style tube radio player.
 * Wooden cabinet, cloth speaker grille, illuminated tuning dial with a red
 * pointer, brass brand plate, amber pilot lamp, and metallic knobs for
 * power (play/pause) and volume. All audio behaviour (Media Session, mutual
 * exclusion, ICY "now playing", vintage SFX) is identical to RadioPlayer.
 */
export const VintageRadio = ({ src, name = "Radio Madre Terra" }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.85);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const nowPlaying = useNowPlaying(isPlaying && !isMuted);

  // Mutual exclusion — if another player claims, pause this one.
  useEffect(
    () =>
      onPlaybackClaim((source) => {
        if (source !== "radio" && audioRef.current && !audioRef.current.paused) {
          audioRef.current.pause();
        }
      }),
    []
  );

  // Broadcast state (kept for potential decorative consumers).
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.dispatchEvent(
      new CustomEvent("mt:radio-state", {
        detail: { playing: isPlaying && !isMuted },
      })
    );
    return undefined;
  }, [isPlaying, isMuted]);

  useEffect(() => {
    const a = audioRef.current;
    if (a) a.volume = volume;
  }, [volume]);

  // Media Session — action handlers, registered once.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) {
      return undefined;
    }
    const ms = navigator.mediaSession;
    const safe = (fn) => () => {
      try {
        fn();
      } catch (_e) {
        /* ignore */
      }
    };
    const handlers = [
      [
        "play",
        safe(() => {
          const a = audioRef.current;
          if (a && a.paused) {
            claimPlayback("radio");
            a.play().catch(() => {});
          }
        }),
      ],
      [
        "pause",
        safe(() => {
          const a = audioRef.current;
          if (a && !a.paused) a.pause();
        }),
      ],
      [
        "stop",
        safe(() => {
          const a = audioRef.current;
          if (a) a.pause();
        }),
      ],
    ];
    handlers.forEach(([action, handler]) => {
      try {
        ms.setActionHandler(action, handler);
      } catch (_e) {
        /* unsupported */
      }
    });
    return () => {
      handlers.forEach(([action]) => {
        try {
          ms.setActionHandler(action, null);
        } catch (_e) {
          /* ignore */
        }
      });
    };
  }, []);

  // Media Session metadata — deduped, only updates when track changes.
  const lastMetaSigRef = useRef(null);
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) {
      return;
    }
    const np = nowPlaying || {};
    const trackTitle =
      (np.artist && np.title && `${np.artist} — ${np.title}`) ||
      np.title ||
      np.raw ||
      "In onda · 24/7";
    const sig = `${name}::${trackTitle}`;
    if (lastMetaSigRef.current === sig) return;
    lastMetaSigRef.current = sig;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: trackTitle,
        artist: name || "MADRETERRA radio",
        album: "Preghiera · Devozione · Spiritualità",
        artwork: [
          { src: "/icon-192.png", sizes: "192x192", type: "image/png" },
          { src: "/icon-512.png", sizes: "512x512", type: "image/png" },
          {
            src: "/icon-maskable-512.png",
            sizes: "512x512",
            type: "image/png",
          },
        ],
      });
    } catch (_e) {
      /* ignore */
    }
  }, [name, nowPlaying]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) {
      return;
    }
    navigator.mediaSession.playbackState =
      isPlaying && !isMuted ? "playing" : "paused";
  }, [isPlaying, isMuted]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const baseTitle = "MADRETERRA radio · Diretta 24/7";
    document.title = isPlaying && !isMuted ? `▶ ${baseTitle}` : baseTitle;
    return () => {
      document.title = baseTitle;
    };
  }, [isPlaying, isMuted]);

  const togglePlay = useCallback(async () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      try {
        setLoading(true);
        setError(null);
        claimPlayback("radio");
        playStartupSequence();
        await a.play();
      } catch (_e) {
        setError("Impossibile avviare la radio.");
        setLoading(false);
      }
    } else {
      playStopClick();
      a.pause();
    }
  }, []);

  const toggleMute = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = !a.muted;
    setIsMuted(a.muted);
  }, []);

  const on = isPlaying && !isMuted;
  const trackLine =
    (nowPlaying?.artist && nowPlaying?.title &&
      `${nowPlaying.artist} — ${nowPlaying.title}`) ||
    nowPlaying?.title ||
    nowPlaying?.raw ||
    (on ? "In onda · 24/7" : "Premi POWER per accendere");

  return (
    <article
      className={`mt-radio ${on ? "mt-radio--on" : ""}`}
      data-testid="vintage-radio"
      aria-label="Radio d'epoca — Madreterra"
    >
      <audio
        ref={audioRef}
        src={src}
        preload="none"
        crossOrigin="anonymous"
        onPlay={() => {
          setIsPlaying(true);
          setLoading(false);
        }}
        onPause={() => setIsPlaying(false)}
        onWaiting={() => setLoading(true)}
        onPlaying={() => setLoading(false)}
        onError={() => {
          setError("Errore di rete sul flusso audio.");
          setLoading(false);
          setIsPlaying(false);
        }}
      />

      {/* Cabinet SVG — wood grain, brand plate, grille, dial */}
      <svg
        viewBox="0 0 640 340"
        className="mt-radio__cabinet"
        role="img"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="mt-wood" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#4a2b18" />
            <stop offset="35%" stopColor="#6b3f22" />
            <stop offset="70%" stopColor="#5a341c" />
            <stop offset="100%" stopColor="#2f1a0d" />
          </linearGradient>
          <linearGradient id="mt-wood-edge" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#7a4a2a" />
            <stop offset="100%" stopColor="#2a170b" />
          </linearGradient>
          <filter id="mt-wood-grain" x="0" y="0" width="100%" height="100%">
            <feTurbulence
              type="turbulence"
              baseFrequency="0.012 0.9"
              numOctaves="2"
              seed="7"
            />
            <feColorMatrix values="0 0 0 0 0.05  0 0 0 0 0.03  0 0 0 0 0.01  0 0 0 0.22 0" />
            <feComposite in2="SourceGraphic" operator="in" />
          </filter>
          <linearGradient id="mt-brass" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f0d38a" />
            <stop offset="40%" stopColor="#c99a4c" />
            <stop offset="100%" stopColor="#8a6524" />
          </linearGradient>
          <linearGradient id="mt-dial" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f4e0a4" />
            <stop offset="50%" stopColor="#e6c675" />
            <stop offset="100%" stopColor="#b6903f" />
          </linearGradient>
          <radialGradient id="mt-dial-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(255,210,120,0.55)" />
            <stop offset="70%" stopColor="rgba(255,180,80,0.10)" />
            <stop offset="100%" stopColor="rgba(255,180,80,0)" />
          </radialGradient>
          <radialGradient id="mt-knob" cx="35%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#f2e1b5" />
            <stop offset="50%" stopColor="#a67932" />
            <stop offset="100%" stopColor="#2a1a0a" />
          </radialGradient>
          <radialGradient id="mt-lamp" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fff2c8" />
            <stop offset="30%" stopColor="#ffcc55" />
            <stop offset="100%" stopColor="rgba(255,150,50,0)" />
          </radialGradient>
        </defs>

        {/* Cabinet base */}
        <rect
          x="4"
          y="4"
          width="632"
          height="332"
          rx="18"
          fill="url(#mt-wood-edge)"
        />
        <rect
          x="14"
          y="14"
          width="612"
          height="312"
          rx="14"
          fill="url(#mt-wood)"
        />
        <rect
          x="14"
          y="14"
          width="612"
          height="312"
          rx="14"
          fill="url(#mt-wood)"
          filter="url(#mt-wood-grain)"
          opacity="0.7"
        />
        {/* Inner bevel */}
        <rect
          x="24"
          y="24"
          width="592"
          height="292"
          rx="10"
          fill="none"
          stroke="rgba(255,220,170,0.10)"
          strokeWidth="1"
        />

        {/* Brand plate (brass) */}
        <g transform="translate(160,32)">
          <rect
            x="0"
            y="0"
            width="320"
            height="34"
            rx="4"
            fill="url(#mt-brass)"
            stroke="#5a3d10"
            strokeWidth="0.8"
          />
          <rect
            x="4"
            y="4"
            width="312"
            height="26"
            rx="2"
            fill="none"
            stroke="rgba(255,255,255,0.25)"
            strokeWidth="0.6"
          />
          <text
            x="160"
            y="22"
            textAnchor="middle"
            fontFamily="Georgia, 'Times New Roman', serif"
            fontStyle="italic"
            fontWeight="700"
            fontSize="16"
            letterSpacing="4"
            fill="#3a2408"
          >
            MADRETERRA RADIO
          </text>
        </g>

        {/* Speaker grille (left) */}
        <g transform="translate(40,90)">
          <rect
            x="0"
            y="0"
            width="270"
            height="210"
            rx="10"
            fill="#1a0d05"
            stroke="rgba(255,220,170,0.10)"
            strokeWidth="0.8"
          />
          <rect
            x="6"
            y="6"
            width="258"
            height="198"
            rx="7"
            fill="url(#mt-brass)"
            opacity="0.35"
          />
          {/* Horizontal cloth slats */}
          {Array.from({ length: 22 }).map((_, i) => (
            <rect
              key={i}
              x="10"
              y={12 + i * 9}
              width="250"
              height="4"
              rx="1.5"
              fill="#0a0503"
              opacity="0.85"
            />
          ))}
          {/* Central emblem — Tau cross */}
          <g transform="translate(135,105)">
            <circle
              cx="0"
              cy="0"
              r="22"
              fill="none"
              stroke="url(#mt-brass)"
              strokeWidth="1.5"
              opacity="0.9"
            />
            <path
              d="M -12 -8 L 12 -8 M 0 -8 L 0 14"
              stroke="url(#mt-brass)"
              strokeWidth="2.5"
              strokeLinecap="round"
              opacity="0.95"
            />
          </g>
        </g>

        {/* Tuning dial (right) */}
        <g transform="translate(330,90)">
          <rect
            x="0"
            y="0"
            width="270"
            height="130"
            rx="8"
            fill="#0a0503"
            stroke="rgba(255,220,170,0.12)"
            strokeWidth="0.8"
          />
          {/* Dial face */}
          <rect
            x="10"
            y="12"
            width="250"
            height="106"
            rx="4"
            fill="url(#mt-dial)"
          />
          {/* Dial amber glow (only when on) */}
          <rect
            x="10"
            y="12"
            width="250"
            height="106"
            rx="4"
            fill="url(#mt-dial-glow)"
            className="mt-radio__dial-glow"
          />
          {/* Frequency scale — AM band */}
          <g
            fontFamily="Georgia, serif"
            fontStyle="italic"
            fontSize="9"
            fill="#3a2408"
          >
            <text x="30" y="30">550</text>
            <text x="70" y="30">700</text>
            <text x="115" y="30">900</text>
            <text x="160" y="30">1200</text>
            <text x="215" y="30">1600 kHz</text>
          </g>
          {/* Tick marks */}
          {Array.from({ length: 26 }).map((_, i) => {
            const x = 20 + i * 9.2;
            const isMajor = i % 5 === 0;
            return (
              <line
                key={i}
                x1={x}
                y1="36"
                x2={x}
                y2={isMajor ? 50 : 44}
                stroke="#2a1a08"
                strokeWidth={isMajor ? 1.3 : 0.7}
              />
            );
          })}
          {/* FM band label */}
          <text
            x="135"
            y="70"
            textAnchor="middle"
            fontFamily="Georgia, serif"
            fontStyle="italic"
            fontSize="8"
            fill="#4a2f10"
            letterSpacing="2"
          >
            ONDE MEDIE · MADRE TERRA
          </text>
          <text
            x="135"
            y="98"
            textAnchor="middle"
            fontFamily="Georgia, serif"
            fontStyle="italic"
            fontSize="14"
            fontWeight="700"
            fill="#3a2408"
          >
            {on ? "IN ONDA" : "OFF"}
          </text>
          {/* Red pointer — sits at MADRETERRA frequency */}
          <g className="mt-radio__needle">
            <line
              x1="150"
              y1="16"
              x2="150"
              y2="112"
              stroke="#c1272d"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
            <circle cx="150" cy="16" r="3" fill="#c1272d" />
          </g>

          {/* Pilot lamp */}
          <g transform="translate(240,10)">
            <circle
              cx="0"
              cy="0"
              r="7"
              fill="#0d0703"
              stroke="rgba(255,255,255,0.15)"
              strokeWidth="0.6"
            />
            <circle
              cx="0"
              cy="0"
              r="4.5"
              fill="url(#mt-lamp)"
              className="mt-radio__lamp"
            />
          </g>
        </g>

        {/* Controls row (right, under dial) */}
        <g transform="translate(330,240)">
          {/* Volume knob */}
          <g transform="translate(50,30)" className="mt-radio__knob-vol">
            <circle cx="0" cy="0" r="30" fill="url(#mt-knob)" />
            <circle
              cx="0"
              cy="0"
              r="30"
              fill="none"
              stroke="rgba(0,0,0,0.6)"
              strokeWidth="0.8"
            />
            <circle
              cx="0"
              cy="0"
              r="24"
              fill="none"
              stroke="rgba(255,220,170,0.28)"
              strokeWidth="0.6"
            />
            {/* Indicator notch */}
            <line
              x1="0"
              y1="-8"
              x2="0"
              y2="-22"
              stroke="#f4e0a4"
              strokeWidth="1.6"
              strokeLinecap="round"
              transform={`rotate(${(volume - 0.5) * 200} 0 0)`}
            />
            <text
              x="0"
              y="52"
              textAnchor="middle"
              fontFamily="Georgia, serif"
              fontStyle="italic"
              fontSize="10"
              fill="#f0d38a"
              opacity="0.8"
            >
              VOLUME
            </text>
          </g>

          {/* Tuning knob (decorative) */}
          <g transform="translate(220,30)">
            <circle cx="0" cy="0" r="30" fill="url(#mt-knob)" />
            <circle
              cx="0"
              cy="0"
              r="30"
              fill="none"
              stroke="rgba(0,0,0,0.6)"
              strokeWidth="0.8"
            />
            <circle
              cx="0"
              cy="0"
              r="24"
              fill="none"
              stroke="rgba(255,220,170,0.28)"
              strokeWidth="0.6"
            />
            <line
              x1="0"
              y1="-8"
              x2="0"
              y2="-22"
              stroke="#f4e0a4"
              strokeWidth="1.6"
              strokeLinecap="round"
              transform="rotate(15 0 0)"
            />
            <text
              x="0"
              y="52"
              textAnchor="middle"
              fontFamily="Georgia, serif"
              fontStyle="italic"
              fontSize="10"
              fill="#f0d38a"
              opacity="0.8"
            >
              SINTONIA
            </text>
          </g>
        </g>
      </svg>

      {/* Interactive overlay — POWER button + volume slider + mute + track */}
      <div className="mt-radio__overlay">
        <div className="mt-radio__now" data-testid="vintage-radio-now">
          {trackLine}
        </div>

        <div className="mt-radio__actions">
          <button
            type="button"
            onClick={togglePlay}
            disabled={loading}
            className="mt-radio__power"
            aria-label={isPlaying ? "Spegni la radio" : "Accendi la radio"}
            aria-pressed={isPlaying}
            data-testid="vintage-radio-power"
          >
            {loading ? (
              <Loader2 size={18} className="mt-spin" />
            ) : (
              <Power size={18} strokeWidth={2.4} />
            )}
            <span>{isPlaying ? "SPEGNI" : "ACCENDI"}</span>
          </button>

          <button
            type="button"
            onClick={toggleMute}
            className="mt-radio__mute"
            aria-label={isMuted ? "Riattiva audio" : "Silenzia"}
            data-testid="vintage-radio-mute"
          >
            {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>

          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            aria-label="Volume"
            className="mt-radio__volume"
            data-testid="vintage-radio-volume"
          />
        </div>

        {error && (
          <div className="mt-radio__error" data-testid="vintage-radio-error">
            {error}
          </div>
        )}
      </div>
    </article>
  );
};

export default VintageRadio;
